/** Per-event routing, quiet hours and the summary schedules. */
import { useApp } from '../../store/AppContext.jsx';
import { NOTIFY_TAGS, NOTIFY_CHANNELS } from '../../constants/defaults.js';
import Icon from '../../lib/icons.jsx';
import { beep, requestNotificationPermission } from '../../domain/alerts.js';
import { Section, Grid, Row, NumberField, CheckGrid, Toggle } from './SettingsBits.jsx';

export default function NotificationsPane() {
  const { S, patchSettings, updateSettings, toast } = useApp();

  const notif = S.notif || {};
  const permission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

  const setRoute = (tag, channel, value) => updateSettings(next => {
    next.notif = { ...(next.notif || {}) };
    next.notif[tag] = { ...(next.notif[tag] || {}), [channel]: value };
  });

  const askPermission = async () => {
    const p = await requestNotificationPermission();
    if (p === 'unsupported') { toast('This browser has no notification support'); return; }
    patchSettings({ desktopAlerts: p === 'granted' });
    toast(p === 'granted' ? 'Desktop alerts on' : 'Permission denied — check browser settings');
  };

  const sendTest = () => {
    beep(S);
    if (S.desktopAlerts && permission === 'granted') {
      try {
        // eslint-disable-next-line no-new
        new Notification('Client Management', { body: 'Test notification — this is working.' });
      } catch { /* blocked */ }
    }
    toast('Test sent');
  };

  return (
    <>
      <Section
        title="Where notifications go"
        hint="In-app always shows on the bell. Email, Slack and the rest are off until switched on per event — email goes to the record's owner, or to every admin when it has none."
      >
        <div className="tscroll">
          <table>
            <thead>
              <tr><th>Event</th>{NOTIFY_CHANNELS.map(c => <th key={c[0]}>{c[1]}</th>)}</tr>
            </thead>
            <tbody>
              {NOTIFY_TAGS.map(([tag, label]) => (
                <tr key={tag}>
                  <td>{label}</td>
                  {NOTIFY_CHANNELS.map(([channel]) => {
                    const current = (notif[tag] || {})[channel];
                    const checked = current !== undefined ? current : channel === 'app';
                    return (
                      <td key={channel}>
                        <input
                          type="checkbox"
                          className="chk"
                          checked={!!checked}
                          onChange={e => setRoute(tag, channel, e.target.checked)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Quiet hours" hint="Desktop notifications and sounds stay silent in this window.">
        <Grid>
          <div style={{ gridColumn: '1/-1' }}>
            <CheckGrid><Toggle label="Enable quiet hours" name="quietOn" /></CheckGrid>
          </div>
          <NumberField label="From" name="quietFrom" unit="hour" min={0} max={23} />
          <NumberField label="Until" name="quietTo" unit="hour" min={0} max={23} />
        </Grid>
      </Section>

      <Section title="Summaries">
        <CheckGrid>
          <Toggle label="Daily team digest" name="digestOn" />
          <Toggle label="Weekly summary on Sunday" name="weeklyOn" />
          <Toggle label="Play a sound on new alerts" name="soundAlerts" />
          <Toggle label="Desktop notifications" name="desktopAlerts" />
        </CheckGrid>
        <Row>
          <button type="button" className="btn" onClick={askPermission}>
            <Icon name="bell" />Enable browser notifications
          </button>
          <button type="button" className="btn" onClick={sendTest}>Send a test</button>
        </Row>
        <p style={{ marginTop: 8 }}>Permission: <b>{permission}</b></p>
      </Section>
    </>
  );
}
